import React, { useState, useEffect, useRef } from 'react';
import { useUser } from '../context/UserContext';
import { CSVLink } from 'react-csv';
import { Pie } from 'react-chartjs-2';
import Chart from 'chart.js/auto';
import './TrialConverted.css';
import { FiChevronDown, FiRefreshCw } from 'react-icons/fi';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { buildDiffPreview, fireAudit } from '../utils/auditClient';

const Trialattended = () => {
  const { userData } = useUser();
  const [searchTerm, setSearchTerm] = useState('');
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [academies, setAcademies] = useState([]);
  const { selectedCenter } = useUser();
  const token = localStorage.getItem('auth-token');

  const baselineRef = useRef({});

  const fetchLeads = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(process.env.REACT_APP_API_TRIAL_VIEW, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch data, Status: ${response.status}`);
      }

      const data = await response.json();

      const apiData = data?.data?.apiData || [];
      const filteredLeads = apiData.filter(lead => lead.isTrialAttended === true);
      const allAcademies = data?.data?.academies || [];

      setLeads(filteredLeads);
      setAcademies(allAcademies);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, [userData]);

  const headers = [
    { label: "Source", key: "source" },
    { label: "Sport", key: "sportId" },
    { label: "Centre", key: "area" },
    { label: "User Name", key: "name" },
    { label: "Phone Number", key: "mobileNumber" },
    { label: "Lead Gen Date", key: "updatedAt" },
    { label: "Trial Date", key: "trialDate" },
    { label: "Trial Time Slot", key: "trialTimeSlot" },
    { label: "Feedback", key: "feedback" },
    { label: "Remark", key: "remarks" },
    { label: "Status", key: "status" },
  ];

  const getAcademyNameById = (academyId) => {
    const academy = academies.find(academy => academy.id === academyId);
    return academy ? academy.name : '';
  };

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setrowsPerPage] = useState(10);
  const today = new Date();
  const tenDaysAgo = new Date();
  tenDaysAgo.setDate(today.getDate() - 10);

  const handleItemsPerPageChange = (event) => {
    setrowsPerPage(Number(event.target.value));
    setCurrentPage(1);
  };

  const formatDate = (date) => date.toISOString().split('T')[0];

  const [filters, setFilters] = useState({
    landingPage: '',
    sport: '',
    centre: selectedCenter || '',
    leadGenStartDate: formatDate(tenDaysAgo),
    leadGenEndDate: formatDate(today),
    trialStartDate: formatDate(tenDaysAgo),
    trialEndDate: formatDate(today),
    status: ''
  });

  useEffect(() => {
    const selectedAcademy = academies.find(academy => academy.id === parseInt(selectedCenter));
    const centreName = selectedAcademy ? selectedAcademy.name : '';
    setFilters((prevFilters) => ({
      ...prevFilters,
      centre: centreName
    }));
  }, [selectedCenter, academies]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value
    }));
    setCurrentPage(1);
  };

  const filteredLeads = leads.filter((lead) => {
    const searchMatch = Object.values(lead)
      .join(' ')
      .toLowerCase()
      .includes(searchTerm.toLowerCase());

    const sourceMatch = filters.source ? lead.source?.toLowerCase().includes(filters.source.toLowerCase()) : true;

    const sportName = lead.sportId === 11 ? 'Swimming' : 'Badminton';
    const sportMatch = filters.sport ? sportName.toLowerCase().includes(filters.sport.toLowerCase()) : true;

    const selectedAcademy = academies.find(academy => academy.name === filters.centre);
    const centreMatch = filters.centre
      ? lead.academyId === selectedAcademy?.id
      : true;

    const accessibleAcademyMatch = userData?.userType === "ACADEMY"
      ? userData.academyIds.includes(lead.academyId)
      : true;

    const leadGenStartDate = filters.leadGenStartDate ? new Date(filters.leadGenStartDate) : null;
    const leadGenEndDate = filters.leadGenEndDate ? new Date(filters.leadGenEndDate) : null;
    const trialStartDate = filters.trialStartDate ? new Date(filters.trialStartDate) : null;
    const trialEndDate = filters.trialEndDate ? new Date(filters.trialEndDate) : null;
    const leadUpdatedAt = new Date(lead.updatedAt);
    const leadTrialDate = new Date(lead.trialDate);

    const leadGenDateMatch =
      (!leadGenStartDate || leadUpdatedAt >= leadGenStartDate) &&
      (!leadGenEndDate || leadUpdatedAt <= leadGenEndDate || leadUpdatedAt.toDateString() === leadGenEndDate.toDateString());

    const trialDateMatch =
      (!trialStartDate || leadTrialDate >= trialStartDate) &&
      (!trialEndDate || leadTrialDate <= trialEndDate || leadTrialDate.toDateString() === trialEndDate.toDateString() || leadTrialDate > trialEndDate);

    const statusMatch = filters.status ? lead.status?.toLowerCase().includes(filters.status.toLowerCase()) : true;

    return (
      searchMatch &&
      sourceMatch &&
      sportMatch &&
      centreMatch &&
      accessibleAcademyMatch &&
      leadGenDateMatch &&
      trialDateMatch &&
      statusMatch
    );
  });

  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const handleSort = (columnKey) => {
    let direction = 'ascending';
    if (sortConfig.key === columnKey && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key: columnKey, direction });
  };

  const sortData = (data) => {
    const { key, direction } = sortConfig;
    if (!key) return data;

    return [...data].sort((a, b) => {
      if (key === 'updatedAt' || key === 'trialDate') {
        const aDate = new Date(a[key]);
        const bDate = new Date(b[key]);
        return direction === 'ascending' ? aDate - bDate : bDate - aDate;
      }
      const aValue = (a[key] ?? '').toString().toLowerCase();
      const bValue = (b[key] ?? '').toString().toLowerCase();
      if (aValue < bValue) return direction === 'ascending' ? -1 : 1;
      if (aValue > bValue) return direction === 'ascending' ? 1 : -1;
      return 0;
    });
  };

  const sortedData = sortData(filteredLeads);
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = sortedData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(sortedData.length / rowsPerPage);

  const handlePageClick = (pageNumber) => setCurrentPage(pageNumber);
  const handleNextPage = () => { if (currentPage < totalPages) setCurrentPage((p) => p + 1); };
  const handlePrevPage = () => { if (currentPage > 1) setCurrentPage((p) => p - 1); };

  const generatePageNumbers = () => {
    const pages = [];
    const totalPagesToShow = 5;
    if (totalPages <= totalPagesToShow) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) pages.push(i);
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  const renderPagination = () => generatePageNumbers().map((page, index) =>
    typeof page === 'number' ? (
      <button
        key={index}
        className={`page-number ${currentPage === page ? 'active' : ''}`}
        onClick={() => handlePageClick(page)}
      >
        {page}
      </button>
    ) : (
      <span key={index} className="pagination-dots">{page}</span>
    )
  );

  const landingPageOptions = [...new Set(leads.map(lead => lead.landingPage))];
  const sportOptions = ['Swimming', 'Badminton'];
  const centreOptions = [...new Set(academies.map(academy => academy.name))];
  const sourceOptions = [...new Set(leads.map(lead => lead.source))];

  const generatePieChartDataForLandingPages = (leads) => {
    const landingPageCounts = leads.reduce((acc, lead) => {
      const source = lead.source || 'Unknown';
      if (!acc[source]) acc[source] = 0;
      acc[source]++;
      return acc;
    }, {});

    return {
      labels: Object.keys(landingPageCounts),
      datasets: [
        {
          data: Object.values(landingPageCounts),
          backgroundColor: ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF'],
          hoverBackgroundColor: ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF'],
        },
      ],
    };
  };

  const handleEditClick = (lead) => {
    const statusMapping = {
      "CALLED": "Called",
      "INTERESTED": "Interested",
      "JUNK": "Junk",
      "CONVERTED": "Converted"
    };

    const normalizedStatus = statusMapping[lead.leadStatus] || lead.leadStatus || '';

    baselineRef.current = {
      remarks: lead.remarks || '',
      status: (normalizedStatus || '').toUpperCase(),
    };

    setSelectedLead({
      ...lead,
      remarks: lead.remarks || '',
      status: normalizedStatus, 
    });

    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const [saving, setSaving] = useState(false);

  const submitUpdate = async (leadId, statusTitleCase, remarks) => {
    const apiUrl = `${process.env.REACT_APP_API_UPDATE_STATUS}/${leadId}`;
    const nextPayload = {
      remarks: remarks || '',
      status: (statusTitleCase || '').toUpperCase(),
    };

    const { digest, html } = buildDiffPreview(baselineRef.current || {}, nextPayload);
    if (!digest?.length) {
      await Swal.fire({
        icon: 'info',
        title: 'Nothing to update',
        text: 'No differences detected.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 2200,
      });
      return;
    }

    const confirm = await Swal.fire({
      title: 'Apply these changes?',
      html,
      showCancelButton: true,
      confirmButtonText: 'Update',
    });
    if (!confirm.isConfirmed) return;

    try {
      setSaving(true);

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(nextPayload)
      });

      fireAudit({
        entity: 'trial_lead',
        affected_id: leadId,
        action: 'update',
        section: 'Marketing',
        subSection: 'Trial Attended',
        businessApi: apiUrl,
        businessMethod: 'POST',
        oldPayload: baselineRef.current || {},
        newPayload: nextPayload,
        extra: { page: 'Trialattended' },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `Failed to update lead, Status: ${response.status}`);
      }

      const data = await response.json();

      setLeads((prevLeads) =>
        prevLeads.map((lead) =>
          lead.id === leadId ? { ...lead, leadStatus: statusTitleCase, remarks } : lead
        )
      );

      baselineRef.current = { ...nextPayload };

      setIsModalOpen(false);

      Swal.fire({
        icon: 'success',
        title: 'Updated',
        text: 'Lead status updated successfully.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 2800,
      });
    } catch (err) {
      setError(err.message);
      Swal.fire({
        icon: 'error',
        title: 'Update failed',
        text: err.message || 'There was an error updating the lead.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3200,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveChanges = () => {
    if (selectedLead) {
      submitUpdate(selectedLead.id, selectedLead.status, selectedLead.remarks);
    }
  };

  const totalEntries = filteredLeads.length;
  const indexOfFirstEntry = totalEntries > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0;
  const indexOfLastEntry = totalEntries > 0 ? Math.min(currentPage * rowsPerPage, totalEntries) : 0;

  const formatTime = (time) => {
    if (!time) return 'N/A';
    const [hour, min] = time.split(":").map(Number);
    const amPm = hour >= 12 ? "PM" : "AM";
    const formattedHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return min === 0 ? `${formattedHour} ${amPm}` : `${formattedHour}:${min} ${amPm}`;
    };

  return (
    <div className="enrollment-container">
      <div className="sales-filters-login">
        <div className="floating-select">
          <select
            name="centre"
            onChange={handleFilterChange}
            value={filters.centre}
            className={`sales-filter-academy ${filters.centre ? "has-value" : ""}`}
          >
            <option value="">Centre</option>
            {userData?.userType === "ACADEMY" ? (
              academies
                .filter(academy => userData.academyIds.includes(academy.id))
                .map((academy) => (
                  <option key={academy.id} value={academy.name}>
                    {academy.name}
                  </option>
                ))
            ) : (
              academies.map((academy) => (
                <option key={academy.id} value={academy.name}>
                  {academy.name}
                </option>
              ))
            )}
          </select>
          {filters.centre && <label htmlFor="centre">Centre</label>}
        </div>

        <div className="floating-select">
          <select
            name="source"
            onChange={handleFilterChange}
            value={filters.source}
            className={`sales-filter-academy ${filters.source ? 'has-value' : ''}`}
          >
            <option value="">Source</option>
            {sourceOptions.map((option, index) => (
              <option key={index} value={option}>{option}</option>
            ))}
          </select>
          {filters.source && <label htmlFor="source">Source</label>}
        </div>

        <div className="floating-select">
          <input
            type="date"
            name="leadGenStartDate"
            value={filters.leadGenStartDate}
            onChange={handleFilterChange}
            onClick={(e) => e.target.showPicker()}
            max={new Date().toISOString().split('T')[0]}
            className={`sales-filter-academy ${filters.leadGenStartDate ? "has-value" : ""}`}
          />
          {filters.leadGenStartDate && <label htmlFor="leadGenStartDate">Lead Gen Start Date</label>}
        </div>

        <div className="floating-select">
          <input
            type="date"
            placeholder="Lead Gen End Date"
            name="leadGenEndDate"
            value={filters.leadGenEndDate}
            onChange={handleFilterChange}
            onClick={(e) => e.target.showPicker()}
            max={new Date().toISOString().split('T')[0]}
            className={`sales-filter-academy ${filters.leadGenEndDate ? "has-value" : ""}`}
          />
          {filters.leadGenEndDate && <label htmlFor="leadGenEndDate">Lead Gen End Date</label>}
        </div>

        <div className="floating-select">
          <input
            type="date"
            placeholder="Trial Start Date"
            name="trialStartDate"
            value={filters.trialStartDate}
            onChange={handleFilterChange}
            onClick={(e) => e.target.showPicker()}
            max={new Date().toISOString().split('T')[0]}
            className={`sales-filter-academy ${filters.trialStartDate ? "has-value" : ""}`}
          />
          {filters.trialStartDate && <label htmlFor="trialStartDate">Trial Start Date</label>}
        </div>

        <div className="floating-select">
          <input
            type="date"
            placeholder="Trial End Date"
            name="trialEndDate"
            value={filters.trialEndDate}
            onChange={handleFilterChange}
            onClick={(e) => e.target.showPicker()}
            max={new Date().toISOString().split('T')[0]}
            className={`sales-filter-academy ${filters.trialEndDate ? "has-value" : ""}`}
          />
          {filters.trialEndDate && <label htmlFor="trialEndDate">Trial End Date</label>}
        </div>

        <div className="floating-select">
          <select
            name="sport"
            onChange={handleFilterChange}
            value={filters.sport}
            className={`sales-filter-academy ${filters.sport ? "has-value" : ""}`}
          >
            <option value="">Sport</option>
            {sportOptions.map((option, index) => (
              <option key={index} value={option}>{option}</option>
            ))}
          </select>
          {filters.sport && <label htmlFor="sport">Sport</label>}
        </div>
      </div>

      {filteredLeads.length > 0 && (
        <div className="unique-charts-container">
          <div className="unique-chart-dropoffs">
            <h3 style={{textAlign:'center'}}>Landing Pages Entries</h3>
            <Pie data={generatePieChartDataForLandingPages(filteredLeads)} />
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div className="search-create-container">
        <div className="input-container">
          <input
            type="text"
            id="searchInput"
            placeholder=""
            className={`search-input ${searchTerm ? "has-value" : ""}`}
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <label htmlFor="searchInput" className="floating-label">
            🔍 Search
          </label>
        </div>
        <CSVLink
          data={filteredLeads}
          headers={headers}
          filename="TrialAttended.csv"
          className="btn btn-primary no-decoration"
        >
          <button className="csv_button" type="button" >
            <span className="button__text">Export CSV</span>
            <span className="button__icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 35 35" className="svg">
                <path d="M17.5,22.131a1.249,1.249,0,0,1-1.25-1.25V2.187a1.25,1.25,0,0,1,2.5,0V20.881A1.25,1.25,0,0,1,17.5,22.131Z"></path>
                <path d="M17.5,22.693a3.189,3.189,0,0,1-2.262-.936L8.487,15.006a1.249,1.249,0,0,1,1.767-1.767l6.751,6.751a.7.7,0,0,0,.99,0l6.751-6.751a1.25,1.25,0,0,1,1.768,1.767l-6.752,6.751A3.191,3.191,0,0,1,17.5,22.693Z"></path>
                <path d="M31.436,34.063H3.564A3.318,3.318,0,0,1,.25,30.749V22.011a1.25,1.25,0,0,1,2.5,0v8.738a.815.815,0,0,0,.814.814H31.436a.815.815,0,0,0,.814-.814V22.011a1.25,1.25,0,1,1,2.5,0v8.738A3.318,3.318,0,0,1,31.436,34.063Z"></path>
              </svg>
            </span>
          </button>
        </CSVLink>
      </div>

      <div className="items-and-dues">
        <select className="items-per-page-select" value={rowsPerPage} onChange={handleItemsPerPageChange}>
          <option value={10}>10 items per page</option>
          <option value={50}>50 items per page</option>
          <option value={75}>75 items per page</option>
          <option value={100}>100 items per page</option>
        </select>

        <div className="pagination-selector">
          <div className="button-group">
            <div className="group-label">Items per page:</div>
            <div className="button-options">
              {[10, 50, 75, 100].map((option) => (
                <button
                  key={option}
                  onClick={() => handleItemsPerPageChange({ target: { value: option } })}
                  className={`page-button ${rowsPerPage === option ? 'active' : ''}`}
                >
                  <span className="button-text">{option}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <button className="tt-action-btn" onClick={fetchLeads} disabled={loading}>
          <FiRefreshCw className={`refresh-icon ${loading ? 'spin' : ''}`} />
          Refresh
        </button>
      </div>

      {loading && (
        <div className="premium-loader">
          <div className="progress-track"><div className="progress-bar"></div></div>
          <div className="status-text">
            <span>L</span><span>O</span><span>A</span><span>D</span><span>I</span><span>N</span><span>G</span>
          </div>
        </div>
      )}
      {error && <div className="error-message">{error}</div>}
      {!loading && !error && filteredLeads.length === 0 && (
        <div className="error-message">No data available</div>
      )}

      {/* Leads Table */}
      <div className="payment-list-scroll-container" style={{  overflowY: 'auto' }}>
        <table className="payment-list-table">
          <thead>
            <tr>
              {headers.map(({ key, label }) => (
                <th key={key} className="challenges-column" onClick={() => handleSort(key)}>
                  <div className="th-content">
                    <span>{label}</span>
                    <FiChevronDown
                      className={`sort-icon ${sortConfig.key === key ? 'active' : ''} ${sortConfig.key === key && sortConfig.direction === 'descending' ? 'desc' : ''}`}
                    />
                  </div>
                </th>
              ))}
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {currentRows.map((lead) => (
              <tr key={lead.id}>
                <td>{lead.source}</td>
                <td>{lead.sportId === 11 ? 'Swimming' : 'Badminton'}</td>
                <td>{getAcademyNameById(lead.academyId)}</td>
                <td>{lead.name}</td>
                <td>{lead.mobileNumber}</td>
                <td>{lead.updatedAt ? new Date(lead.updatedAt).toISOString().split("T")[0] : "N/A"}</td>
                <td>{lead.trialDate ? new Date(lead.trialDate).toISOString().split("T")[0] : "N/A"}</td>
                <td>
                  {lead.startTime && lead.endTime
                    ? `${formatTime(lead.startTime)} - ${formatTime(lead.endTime)}`
                    : "N/A"}
                </td>
                <td>{lead.feedback || '-'}</td>
                <td>{lead.remarks || '-'}</td>
                <td>{lead.leadStatus}</td>
                <td>
                  <button className="edit-button" onClick={() => handleEditClick(lead)}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 3.487a2.12 2.12 0 0 1 3.001 3.001L7.5 18.85l-4 1 1-4L16.862 3.487z" />
                        </svg>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="pagination">
        <div className="showing-entries">
          Showing {indexOfFirstEntry} to {indexOfLastEntry} of {totalEntries} entries
        </div>
        <button className="prev-button" onClick={handlePrevPage} disabled={currentPage === 1}>Previous</button>
        {renderPagination()}
        <button className="next-button" onClick={handleNextPage} disabled={currentPage === totalPages}>Next</button>
      </div>

      {/* Modal for Editing Lead */}
      {isModalOpen && (
        <div className="firstb-modal-overlay" onClick={handleModalClose}>
          <div className="firstb-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Edit Status</h3>
            <div className="firstb-modal-row">
              <label htmlFor="remark">Remark:</label>
              <textarea
                id="remark"
                value={selectedLead?.remarks || ''}
                onChange={(e) => setSelectedLead({ ...selectedLead, remarks: e.target.value })}
              />
            </div>
            <div className="firstb-modal-row">
              <label>Status:</label>
              <div className="firstb-status-radio-group">
                {['Called','Interested','Junk','Converted'].map(opt => (
                  <div key={opt}>
                    <input
                      type="radio"
                      id={opt.toLowerCase()}
                      name="status"
                      value={opt}
                      checked={selectedLead?.status === opt}
                      onChange={(e) => setSelectedLead({ ...selectedLead, status: e.target.value })}
                    />
                    <label htmlFor={opt.toLowerCase()}>{opt}</label>
                  </div>
                ))}
              </div>
            </div>
            <div className="modal-actions">
              <button className="save-button-marketing" onClick={handleSaveChanges}>Save</button>
              <button className="cancel-button-marketing" onClick={handleModalClose}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Trialattended;
